<script lang="ts" setup>
import { computed } from 'vue';

export interface SegmentCardItem {
  label: string;
  value: string | number;
}

interface Props {
  title: string;
  items?: SegmentCardItem[];
  selectable?: boolean;
  selected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  items: undefined,
  selectable: false,
  selected: false,
});

const emit = defineEmits<{
  select: [];
}>();

const rootStyle = computed(() => {
  if (!props.selected) return undefined;
  return { borderColor: 'var(--ui2-background-accent-primary)' };
});

function handleClick() {
  if (props.selectable) {
    emit('select');
  }
}
</script>

<template>
  <component
    :is="selectable ? 'button' : 'div'"
    :type="selectable ? 'button' : undefined"
    :class="[
      $style.root,
      selectable && $style.selectable,
      selected && $style.selected,
    ]"
    :style="rootStyle"
    data-ui-component="UiSegmentCard"
    @click="handleClick"
  >
    <div :class="$style.title">{{ title }}</div>

    <div v-if="items?.length" :class="$style.itemsList">
      <div v-for="(item, index) in items" :key="index" :class="$style.itemRow">
        <span :class="$style.itemLabel">{{ item.label }}</span>
        <span :class="$style.itemValue">{{ item.value }}</span>
      </div>
    </div>
  </component>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/typography';

.root {
  @include shared.reset-button;

  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--ui2-background-primary);
  border: 1px solid var(--ui2-stroke-secondary);
  border-radius: 12px;
  padding: 16px;
  width: 100%;
  text-align: left;
  font-family: var(--ui2-font-family, 'Poppins', sans-serif);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus-visible {
    outline: 2px solid var(--ui2-state-focus, #085cd9);
    outline-offset: -2px;
  }
}

.selectable {
  cursor: pointer;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.selected {
  border-width: 1.5px;
  padding: 15.5px;
}

.title {
  @include typography.style('data-xs-semibold');

  color: var(--ui2-content-primary);
  word-break: break-word;
}

.itemsList {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 12px;
}

.itemRow {
  @include typography.style('data-xs-regular');

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}

.itemLabel {
  color: var(--ui2-content-secondary);
}

.itemValue {
  @include typography.style('data-xs-medium');

  color: var(--ui2-content-primary);
}
</style>
